import type { OrganizationDepartmentRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm, zodAddressSchema, zodPhoneNumberSchema } from 'noddi-ui-web';
import { z } from 'zod';
import { TanstackFormAddressPicker } from '../../form/tanstack/TanstackFormAddressPicker/TanstackFormAddressPicker';
import { TanstackAccountAsyncSelect } from '../../form/tanstack/features/Account/TanstackAccountAsyncSelect';
import { invalidateDepartmentQueries } from './invalidateDepartmentQueries';

const FORM_ID = 'create-department-form';

const validationSchema = z.object({
  name: z.string().min(1, 'Required'),
  user: z
    .object({ value: z.number(), label: z.string() })
    .optional()
    .refine((val) => !!val, { message: 'Required' }),
  email: z.string().min(1, 'Required').pipe(z.email('Invalid email')),
  phoneNumber: zodPhoneNumberSchema,
  website: z.url('Invalid URL').or(z.literal('')),
  address: zodAddressSchema.refine((val) => !!val, { message: 'Address is required' })
});

type FormValues = z.input<typeof validationSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  onCreated?: (department: OrganizationDepartmentRecord) => void;
}

export const CreateDepartmentSheet = ({ open, onOpenChange, organizationId, onCreated }: Props) => {
  const { mutateAsync: createDepartment } = noddiAsync.usePost({
    type: URLKeys.postDepartment,
    queryConfig: {
      onSuccess: (response) => {
        invalidateDepartmentQueries();
        onCreated?.(response.data);
        onOpenChange(false);
      }
    }
  });

  const defaultValues: FormValues = {
    name: '',
    user: undefined,
    email: '',
    phoneNumber: '',
    website: '',
    address: undefined as unknown as { id: number; name: string }
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await createDepartment({
        name: value.name.trim(),
        userId: value.user!.value,
        organizationId,
        addressId: value.address.id,
        contactDetails: {
          email: value.email,
          // Parse to apply the schema's whitespace-trim — TanStack Form never
          // writes transform output back into form state.
          phoneNumber: zodPhoneNumberSchema.parse(value.phoneNumber),
          ...(value.website && { website: value.website })
        }
      });
      form.reset();
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen);
          if (!nextOpen) {
            form.reset();
          }
        }}
        title='Create department'
        description='Create a new department for this organization. The selected user will be the owner of the new department.'
        side='right'
        footer={<form.SubscribeButton className='w-full' formId={FORM_ID} label='Create department' startIcon='Save' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className='flex flex-col gap-4'
        >
          <form.AppField name='name'>{(field) => <field.Input label='Department name' required />}</form.AppField>
          <form.AppField name='user'>{() => <TanstackAccountAsyncSelect label='Owner (account)' />}</form.AppField>
          <form.AppField name='address'>{() => <TanstackFormAddressPicker label='Address' />}</form.AppField>
          <h3 className='text-lg font-medium'>Contact details</h3>
          <form.AppField name='email'>{(field) => <field.Input label='Email' required />}</form.AppField>
          <form.AppField name='phoneNumber'>{(field) => <field.Input label='Phone number' required />}</form.AppField>
          <form.AppField name='website'>{(field) => <field.Input label='Website' />}</form.AppField>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
